'use client';

import { Logo } from '@/components/icons';
import { SmartLink } from '@/components/smart-link';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@/providers/auth-provider';
import {
  BrainCircuitIcon,
  GemIcon,
  GiftIcon,
  HistoryIcon,
  LanguagesIcon,
  NewspaperIcon,
  SearchIcon,
} from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

const bottomLinks = [
  {
    label: '邀请有礼',
    href: '/my-invite',
    icon: GiftIcon,
    highlight: true,
    protected: true,
  },
  {
    label: '套餐&价格',
    href: '/pricing/subscribe',
    icon: GemIcon,
  },
  {
    label: '历史记录',
    href: '/histories',
    icon: HistoryIcon,
    protected: true,
  },
];

export function SideMenu() {
  const { user } = useAuth();
  const pathname = usePathname();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const mainLinks: Array<{
    href: string;
    icon: typeof SearchIcon;
    label: string;
    protected?: boolean;
  }> = [
    {
      label: '文献检索',
      href: '/',
      icon: SearchIcon,
    },
    {
      label: '文档翻译',
      href: user ? '/translate/upload' : '/translate',
      icon: LanguagesIcon,
    },
    {
      label: '深度研究',
      href: '/deep-research',
      icon: BrainCircuitIcon,
    },
    {
      label: '学术资讯',
      href: '/news',
      icon: NewspaperIcon,
    },
  ];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <SmartLink href="/" mode="always">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                  <Logo className="w-6! h-6!" />
                </div>
                {!isCollapsed && (
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-semibold text-foreground">Suppr</span>
                    <span className="text-xs text-muted-foreground">
                      超能文献
                    </span>
                  </div>
                )}
              </SmartLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-3">
          <SidebarGroupContent>
            <SidebarMenu>
              {mainLinks.map((link) => (
                <SidebarMenuItem key={link.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={link.href === pathname}
                    tooltip={link.label}
                  >
                    <SmartLink
                      href={link.href}
                      authenticated={!!user}
                      mode={link.protected ? 'guarded' : 'always'}
                    >
                      <link.icon />
                      <span>{link.label}</span>
                    </SmartLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarSeparator />
        <SidebarGroup className="px-3">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname.startsWith('/landing/zotero')}
                  tooltip="Zotero 插件"
                >
                  <SmartLink href="/landing/zotero" mode="always">
                    <Image
                      src="/img/zotero-logo.png"
                      width={16}
                      height={16}
                      alt="Suppr Zotero 插件"
                      sizes="16px"
                    />
                    <span>Zotero 插件</span>
                  </SmartLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="px-3">
        <SidebarMenu>
          {bottomLinks.map((link) => (
            <SidebarMenuItem key={link.href}>
              <SidebarMenuButton
                asChild
                isActive={pathname.startsWith(link.href)}
                tooltip={link.label}
              >
                <SmartLink
                  href={link.href}
                  authenticated={!!user}
                  mode={link.protected ? 'guarded' : 'always'}
                >
                  <link.icon
                    className={
                      link.highlight
                        ? 'text-primary animate-bounce group-hover/menu-item:animate-none'
                        : ''
                    }
                  />
                  <span>{link.label}</span>
                </SmartLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
